import React, { useState, useEffect } from "react";
import Template from "../../components/Template";
import { useNavigate, useParams } from "react-router-dom";
import api from "../../services/api";
import ConfirmationModal from "../../components/ConfirmationModal";
import SuccessModal from "../../components/SuccessModal";
import AlertDialog from "../../components/AlertDialog";
import FormLabel from "../../components/FormLabel";
import config from "../../config";
import { maskCpfCnpj, maskCurrency, formatCurrencyFromDb } from "../../utils/masks";
import { parseCurrencyToFloat } from "../../utils/utils";

const EditarNotaFiscalServico = () => {
    const navigate = useNavigate();
    const { id } = useParams(); // ID da NFSe a editar

    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const [showSuccessDialog, setShowSuccessDialog] = useState(false);
    const [message, setMessage] = useState("");

    // Estado do formulário
    const [formData, setFormData] = useState({
        xml: null,
        pdf: null,
        boleto: null,
        obra: "",
        // Campos manuais (usados se o XML não for enviado ou para edição manual)
        valorNota: "",
        descricaoNota: "",
        dataEmissaoManual: "",
        chaveManual: "",
        numeroNota: "",
        cnpjEmitente: "",
        nomeEmitente: "",
        enderecoEmitente: "",
        razaoSocialEmitente: "",
        numeroEndereco: "",
        bairro: "",
        municipio: "",
        uf: "",
        cep: "",
        complemento: "",
        observacao: ""
    });

    // Flag para indicar se o usuário optou por preencher manualmente (sem XML)
    const [isManual, setIsManual] = useState(true);

    // Lista de obras para o select (opcional)
    const [obras, setObras] = useState([]);

    // Carrega os dados existentes da NFSe
    useEffect(() => {
        const fetchNFSe = async () => {
            try {
                const response = await api.get(`/notasfiscaisservicos/${id}`);
                const data = response.data;
                setFormData({
                    xml: null, // Não podemos setar valor em file input
                    pdf: null,
                    boleto: null,
                    obra: data.obra?.id || "",
                    valorNota: data.valorServico ? formatCurrencyFromDb(data.valorServico) : "",
                    descricaoNota: data.descricaoServico || "",
                    dataEmissaoManual: data.dataEmissao ? data.dataEmissao.split("T")[0] : "",
                    chaveManual: data.chave || "",
                    numeroNota: data.numero || "",
                    cnpjEmitente: data.fornecedor?.cpfCnpj || "",
                    nomeEmitente: data.fornecedor?.nomeFornecedor || "",
                    razaoSocialEmitente: data.fornecedor?.razaoSocial || "",
                    enderecoEmitente: data.fornecedor?.endereco || "",
                    numeroEndereco: data.fornecedor?.numeroEndereco || "",
                    bairro: data.fornecedor?.bairro || "",
                    municipio: data.fornecedor?.municipio || "",
                    uf: data.fornecedor?.uf || "",
                    cep: data.fornecedor?.cep || "",
                    complemento: data.fornecedor?.complemento || "",
                    observacao: data.observacao || "",
                });
                // Se não houver XML armazenado, ativa o modo manual
                if (!data.xml) {
                    setIsManual(true);
                }
            } catch (error) {
                console.error("Erro ao carregar NFSe:", error);
                setMessage("Erro ao carregar os dados da NFSe. Tente novamente.");
            }
        };
        fetchNFSe();
    }, [id]);

    // Carrega a lista de obras
    useEffect(() => {
        const fetchObras = async () => {
            try {
                const response = await api.get("/obras/listar/todas");
                setObras(response.data);
            } catch (error) {
                console.error("Erro ao carregar obras:", error);
            }
        };
        fetchObras();
    }, []);

    // Busca os dados do emitente pelo CNPJ
    const fetchEmitenteData = async (cnpj) => {
        if (!cnpj) {
            console.warn("CNPJ/CPF está vazio ou nulo.");
            return;
        }

        const cleanCnpj = cnpj.replace(/[^\d]/g, '');

        if (cleanCnpj.length === 0) {
            console.warn("CNPJ/CPF contém caracteres inválidos.");
            return;
        }

        try {
            const response = await api.get(`/fornecedores/cnpj/${cleanCnpj}`);
            if (response.data) {
                setFormData((prevData) => ({
                    ...prevData,
                    nomeEmitente: response.data.nomeFornecedor || "",
                    enderecoEmitente: response.data.endereco || "",
                    numeroEndereco: response.data.numeroEndereco || "",
                    bairro: response.data.bairro || "",
                    municipio: response.data.municipio || "",
                    uf: response.data.uf || "",
                    cep: response.data.cep || "",
                    complemento: response.data.complemento || "",
                }));
            }
        } catch (error) {
            console.error("Emitente não encontrado.", error);
            setFormData((prevData) => ({
                ...prevData,
                nomeEmitente: "",
                enderecoEmitente: "",
                numeroEndereco: "",
                bairro: "",
                municipio: "",
                uf: "",
                cep: "",
                complemento: "",
            }));
        }
    };

    // Handlers
    const handleFileChange = (e) => {
        const { name, files } = e.target;
        setFormData((prevData) => ({
            ...prevData,
            [name]: files[0],
        }));
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prevData) => ({
            ...prevData,
            [name]: value,
        }));
    };

    const handleCpfCnpjChange = (e) => {
        const value = e.target.value;
        const maskedValue = maskCpfCnpj(value);
        setFormData((prevData) => ({
            ...prevData,
            cnpjEmitente: maskedValue,
        }));
    };

    const handleCnpjBlur = () => {
        if (formData.cnpjEmitente) {
            fetchEmitenteData(formData.cnpjEmitente);
        }
    };

    const handleManualModeToggle = (e) => {
        setIsManual(e.target.checked);
    };

    // Handler para o campo "Valor da Nota" com máscara monetária
    const handleCurrencyChange = (e) => {
        const value = e.target.value;
        const masked = maskCurrency(value);
        setFormData((prevData) => ({
            ...prevData,
            valorNota: masked,
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!isManual && !formData.xml) {
            setMessage("O arquivo XML é obrigatório, ou marque a opção de preenchimento manual.");
            return;
        }
        setShowConfirmDialog(true);
    };

    const handleConfirm = async () => {
        setShowConfirmDialog(false);
        try {
            const data = new FormData();
            if (!isManual && formData.xml) {
                data.append("xml", formData.xml);
            } else {
                data.append("valorNota", parseCurrencyToFloat(formData.valorNota));
                data.append("descricaoNota", formData.descricaoNota);
                data.append("dataEmissao", formData.dataEmissaoManual);
                data.append("chave", formData.chaveManual);
                data.append("numeroNota", formData.numeroNota);
                data.append("cnpjEmitente", formData.cnpjEmitente);
                data.append("razaoSocialEmitente", formData.razaoSocialEmitente);
                data.append("nomeEmitente", formData.nomeEmitente);
                data.append("enderecoEmitente", formData.enderecoEmitente);
                data.append("numeroEndereco", formData.numeroEndereco);
                data.append("bairro", formData.bairro);
                data.append("municipio", formData.municipio);
                data.append("uf", formData.uf);
                data.append("cep", formData.cep);
                data.append("complemento", formData.complemento);
                data.append("observacao", formData.observacao);
            }
            if (formData.xml) {
                data.append("xml", formData.xml);
            }
            if (formData.pdf) {
                data.append("pdf", formData.pdf);
            }
            if (formData.boleto) {
                data.append("boleto", formData.boleto);
            }
            if (formData.obra) {
                data.append("obraId", formData.obra);
            }
            await api.post(`/notasfiscaisservicos/editar/${id}/import`, data, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setShowSuccessDialog(true);
            setTimeout(() => {
                setShowSuccessDialog(false);
                navigate("/notasfiscaisservicos");
            }, 3000);
        } catch (error) {
            console.error("Erro ao atualizar NFSe:", error);
            setMessage("Erro ao atualizar a NFSe. Verifique os dados e tente novamente.");
        }
    };

    const handleCancel = () => {
        setShowConfirmDialog(false);
    };

    const handleBack = () => {
        navigate("/notasfiscaisservicos");
    };

    return (
        <Template>
            <form onSubmit={handleSubmit} className="w-full px-4 py-6 flex flex-col gap-8">
                {/* Título */}
                <div>
                    <h1 className="text-2xl text-stone-800 font-medium">Editar Nota Fiscal de Serviço</h1>
                </div>

                {/* Se não estiver em modo manual, exibe upload do XML */}

                <div className="flex flex-col gap-4">
                    <h2 className="text-base text-stone-600 font-medium">Arquivo XML</h2>
                    <div className="flex flex-col gap-1.5">
                        <FormLabel htmlFor="xml">
                            Arquivo XML
                        </FormLabel>
                        <div className="relative flex items-center gap-4">
                            <input
                                id="xml"
                                name="xml"
                                type="file"
                                accept=".xml"
                                onChange={handleFileChange}
                                className="absolute inset-0 opacity-0 cursor-pointer"
                            />
                            <div className="w-full pl-3 pr-14 py-2 bg-white rounded-md border border-slate-300 flex items-center gap-2">
                                <span className="text-base text-slate-400">
                                    {formData.xml ? formData.xml.name : "Selecionar arquivo XML"}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>


                {/* Upload dos arquivos (PDF da Nota e PDF do Boleto) - sempre visíveis */}
                <div className="flex flex-col gap-4">
                    <h2 className="text-base text-stone-600 font-medium">Arquivos Adicionais</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* PDF da Nota */}
                        <div className="flex flex-col gap-1.5">
                            <FormLabel htmlFor="pdf" isRequired={false}>
                                Arquivo PDF da Nota (Opcional)
                            </FormLabel>
                            <div className="relative flex items-center gap-4">
                                <input
                                    id="pdf"
                                    name="pdf"
                                    type="file"
                                    accept=".pdf"
                                    onChange={handleFileChange}
                                    className="absolute inset-0 opacity-0 cursor-pointer"
                                />
                                <div className="w-full pl-3 pr-14 py-2 bg-white rounded-md border border-slate-300 flex items-center gap-2">
                                    <span className="text-base text-slate-400">
                                        {formData.pdf ? formData.pdf.name : "Selecionar arquivo PDF da nota"}
                                    </span>
                                </div>
                            </div>
                        </div>
                        {/* PDF do Boleto */}
                        <div className="flex flex-col gap-1">
                            <FormLabel htmlFor="boleto" isRequired={false}>
                                Arquivo PDF do Boleto (Opcional)
                            </FormLabel>
                            <div className="relative">
                                <input
                                    type="file"
                                    name="boleto"
                                    accept=".pdf"
                                    onChange={handleFileChange}
                                    className="absolute inset-0 opacity-0 cursor-pointer"
                                />
                                <div className="w-full pl-3 pr-14 py-2 bg-white rounded-md border border-slate-300 flex items-center">
                                    <span className="text-base text-slate-400">
                                        {formData.boleto ? formData.boleto.name : "Selecionar arquivo PDF do boleto"}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Campo Opcional para Obra */}
                <div className="flex flex-col gap-4">
                    <FormLabel htmlFor="obra" isRequired={false}>
                        Obra (Opcional)
                    </FormLabel>
                    <select
                        name="obra"
                        id="obra"
                        value={formData.obra}
                        onChange={handleInputChange}
                        className="px-3 py-2 bg-white rounded-md border border-slate-300 text-base text-slate-700"
                    >
                        <option value="">Selecionar</option>
                        {obras.map((obra) => (
                            <option key={obra.id} value={obra.id}>
                                {obra.nomeProjeto.toUpperCase()}
                            </option>
                        ))}
                    </select>
                </div>

                {/* Se estiver no modo manual, exibe os campos manuais para preenchimento */}
                {isManual && (
                    <div className="flex flex-col gap-4">
                        <h2 className="text-base text-stone-600 font-medium">Dados da Nota</h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="flex flex-col gap-1">
                                <FormLabel htmlFor="valorNota" isRequired={true}>
                                    Valor da Nota
                                </FormLabel>
                                <input
                                    type="text"
                                    name="valorNota"
                                    id="valorNota"
                                    value={formData.valorNota}
                                    onChange={handleCurrencyChange}
                                    className="px-3 py-2 bg-white rounded-md border border-slate-300"
                                    placeholder="Digite o valor da nota"
                                    required
                                />
                            </div>
                            <div className="flex flex-col gap-1">
                                <FormLabel htmlFor="descricaoNota" isRequired={true}>
                                    Descrição da Nota
                                </FormLabel>
                                <input
                                    type="text"
                                    name="descricaoNota"
                                    id="descricaoNota"
                                    value={formData.descricaoNota}
                                    onChange={handleInputChange}
                                    className="px-3 py-2 bg-white rounded-md border border-slate-300"
                                    placeholder="Digite a descrição da nota"
                                    required
                                />
                            </div>
                            <div className="flex flex-col gap-1">
                                <FormLabel htmlFor="dataEmissaoManual" isRequired={true}>
                                    Data de Emissão
                                </FormLabel>
                                <input
                                    type="date"
                                    name="dataEmissaoManual"
                                    id="dataEmissaoManual"
                                    value={formData.dataEmissaoManual}
                                    onChange={handleInputChange}
                                    className="px-3 py-2 bg-white rounded-md border border-slate-300"
                                    required
                                />
                            </div>
                            <div className="flex flex-col gap-1">
                                <FormLabel htmlFor="chaveManual" isRequired={true}>
                                    Chave da Nota
                                </FormLabel>
                                <input
                                    type="text"
                                    name="chaveManual"
                                    id="chaveManual"
                                    value={formData.chaveManual}
                                    onChange={handleInputChange}
                                    className="px-3 py-2 bg-white rounded-md border border-slate-300"
                                    placeholder="Digite a chave da nota"
                                    required
                                />
                            </div>
                            <div className="flex flex-col gap-1">
                                <FormLabel htmlFor="numeroNota" isRequired={false}>
                                    Número da Nota
                                </FormLabel>
                                <input
                                    type="number"
                                    name="numeroNota"
                                    id="numeroNota"
                                    value={formData.numeroNota}
                                    onChange={handleInputChange}
                                    className="px-3 py-2 bg-white rounded-md border border-slate-300"
                                    placeholder="Digite o número da nota"
                                />
                            </div>
                        </div>

                        <h2 className="text-base text-stone-600 font-medium mt-4">Dados do Emitente</h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="flex flex-col gap-1">
                                <FormLabel htmlFor="cnpjEmitente" isRequired={true}>
                                    CNPJ do Emitente
                                </FormLabel>
                                <input
                                    type="text"
                                    name="cnpjEmitente"
                                    id="cnpjEmitente"
                                    value={formData.cnpjEmitente}
                                    onChange={handleCpfCnpjChange}
                                    onBlur={handleCnpjBlur}
                                    className="px-3 py-2 bg-white rounded-md border border-slate-300"
                                    placeholder="Digite o CNPJ do emitente"
                                    required
                                />
                            </div>
                            <div className="flex flex-col gap-1">
                                <FormLabel htmlFor="nomeEmitente" isRequired={true}>
                                    Nome do Emitente
                                </FormLabel>
                                <input
                                    type="text"
                                    name="nomeEmitente"
                                    id="nomeEmitente"
                                    value={formData.nomeEmitente}
                                    onChange={handleInputChange}
                                    className="px-3 py-2 bg-white rounded-md border border-slate-300"
                                    placeholder="Nome do emitente"
                                    required
                                />
                            </div>
                            <div className="flex flex-col gap-1">
                                <FormLabel htmlFor="razaoSocial">
                                    Razão Social Emitente
                                </FormLabel>
                                <input
                                    type="text"
                                    name="razaoSocialEmitente"
                                    id="razaoSocialEmitente"
                                    value={formData.razaoSocialEmitente}
                                    onChange={handleInputChange}
                                    className="px-3 py-2 bg-white rounded-md border border-slate-300"
                                    placeholder="Razão Social do Emitente"
                                />
                            </div>
                            <div className="flex flex-col gap-1">
                                <FormLabel htmlFor="enderecoEmitente" isRequired={false}>
                                    Endereço do Emitente
                                </FormLabel>
                                <input
                                    type="text"
                                    name="enderecoEmitente"
                                    id="enderecoEmitente"
                                    value={formData.enderecoEmitente}
                                    onChange={handleInputChange}
                                    className="px-3 py-2 bg-white rounded-md border border-slate-300"
                                    placeholder="Endereço completo"
                                />
                            </div>
                            <div className="flex flex-col gap-1">
                                <FormLabel htmlFor="numeroEndereco" isRequired={false}>
                                    Número
                                </FormLabel>
                                <input
                                    type="text"
                                    name="numeroEndereco"
                                    id="numeroEndereco"
                                    value={formData.numeroEndereco}
                                    onChange={handleInputChange}
                                    className="px-3 py-2 bg-white rounded-md border border-slate-300"
                                    placeholder="Número"
                                />
                            </div>
                            <div className="flex flex-col gap-1">
                                <FormLabel htmlFor="bairro" isRequired={false}>
                                    Bairro
                                </FormLabel>
                                <input
                                    type="text"
                                    name="bairro"
                                    id="bairro"
                                    value={formData.bairro}
                                    onChange={handleInputChange}
                                    className="px-3 py-2 bg-white rounded-md border border-slate-300"
                                    placeholder="Bairro"
                                />
                            </div>
                            <div className="flex flex-col gap-1">
                                <FormLabel htmlFor="municipio" isRequired={false}>
                                    Município
                                </FormLabel>
                                <input
                                    type="text"
                                    name="municipio"
                                    id="municipio"
                                    value={formData.municipio}
                                    onChange={handleInputChange}
                                    className="px-3 py-2 bg-white rounded-md border border-slate-300"
                                    placeholder="Município"
                                />
                            </div>
                            <div className="flex flex-col gap-1">
                                <FormLabel htmlFor="uf" isRequired={false}>
                                    UF
                                </FormLabel>
                                <input
                                    type="text"
                                    name="uf"
                                    id="uf"
                                    value={formData.uf}
                                    onChange={handleInputChange}
                                    className="px-3 py-2 bg-white rounded-md border border-slate-300"
                                    placeholder="UF"
                                />
                            </div>
                            <div className="flex flex-col gap-1">
                                <FormLabel htmlFor="cep" isRequired={false}>
                                    CEP
                                </FormLabel>
                                <input
                                    type="text"
                                    name="cep"
                                    id="cep"
                                    value={formData.cep}
                                    onChange={handleInputChange}
                                    className="px-3 py-2 bg-white rounded-md border border-slate-300"
                                    placeholder="CEP"
                                />
                            </div>
                            <div className="flex flex-col gap-1">
                                <FormLabel htmlFor="complemento" isRequired={false}>
                                    Complemento
                                </FormLabel>
                                <input
                                    type="text"
                                    name="complemento"
                                    id="complemento"
                                    value={formData.complemento}
                                    onChange={handleInputChange}
                                    className="px-3 py-2 bg-white rounded-md border border-slate-300"
                                    placeholder="Complemento"
                                />
                            </div>
                        </div>
                        {/* Observações */}
                        <div className="flex flex-col gap-4">
                            <h2 className="text-base text-stone-600 font-medium">Observações</h2>
                            <textarea
                                name="observacao"
                                rows={4}
                                value={formData.observacao}
                                onChange={handleInputChange}
                                className="w-full px-3 py-2 bg-white rounded-md border border-slate-300 text-sm text-slate-700 resize-none"
                                placeholder="Digite suas observações aqui..."
                            />
                        </div>
                    </div>
                )}

                {message && (
                    <AlertDialog
                        isVisible={true}
                        message={message}
                        onClose={() => setMessage("")}
                    />
                )}

                <div className="flex items-center w-full gap-4">
                    <button
                        type="button"
                        onClick={handleBack}
                        className="px-4 py-2 bg-stone-600 text-white text-sm font-medium rounded-md flex items-center gap-2"
                    >
                        <img className="w-4 h-4" src="/images/icon/arrow-left.png" alt="Voltar" />
                        <span>Voltar</span>
                    </button>
                    <button
                        type="submit"
                        className="px-4 py-2 bg-stone-600 text-white text-sm font-medium rounded-md flex items-center gap-2"
                    >
                        <img className="w-4 h-4" src="/images/icon/save.png" alt="Salvar" />
                        <span>Salvar</span>
                    </button>
                </div>

                <ConfirmationModal
                    isVisible={showConfirmDialog}
                    onConfirm={handleConfirm}
                    onCancel={handleCancel}
                    message="Deseja atualizar esta NFSe?"
                    textButtonConfirm="Atualizar"
                />
                <SuccessModal
                    isVisible={showSuccessDialog}
                    message="NFSe atualizada com sucesso!"
                    onClose={() => setShowSuccessDialog(false)}
                />
            </form>
        </Template>
    );
};

export default EditarNotaFiscalServico;
